/**
 * CalendarPage.jsx — Calendar view (MUI)
 *
 * Desktop: full CalendarView (month/week/year)
 * Mobile: MobileCalendarView (month/day/agenda)
 */

import { useState, useMemo } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { useMobile } from '../hooks/useMobile';
import { useSettings } from '../hooks/useSettings';
import { useAuth } from '../hooks/useAuth';
import { useRBAC } from '../hooks/useRBAC';
import CalendarView from '../components/calendar/CalendarView';
import MobileCalendarView from '../components/calendar/MobileCalendarView';

// Gate on settings so CalendarPageContent mounts with correct defaults
export default function CalendarPage() {
  const { data: settings, isLoading: settingsLoading } = useSettings();
  if (settingsLoading) return null;
  return <CalendarPageContent settings={settings} />;
}

function CalendarPageContent({ settings }) {
  const { isMobile } = useMobile();
  const { profile } = useAuth();
  const { isAdmin } = useRBAC();

  // Per-user calendar view pref → company default → 'month'
  const defaultView = useMemo(() => {
    const d = settings?.data || {};
    const userPref = profile?.id && d.userPrefs?.[profile.id]?.calendarView;
    return userPref || d.defaultCalendarView || 'month';
  }, [settings, profile]);

  // Per-user calendar scope pref → role-based default (admin/super_admin → 'company')
  const defaultScope = useMemo(() => {
    const d = settings?.data || {};
    const userPref = profile?.id && d.userPrefs?.[profile.id]?.calendarScope;
    if (userPref) return userPref;
    return isAdmin ? 'company' : 'mine';
  }, [settings, profile, isAdmin]);

  const [calendarScope, setCalendarScope] = useState(defaultScope);

  const scopeToggle = (
    <ToggleButtonGroup
      value={calendarScope}
      exclusive
      onChange={(e, v) => { if (v) setCalendarScope(v); }}
      size="small"
      sx={{ height: 28 }}
    >
      <ToggleButton value="mine" sx={{ fontSize: '0.7rem', px: 1.5, textTransform: 'none', gap: 0.5 }}>
        <PersonIcon sx={{ fontSize: 14 }} /> My Cal
      </ToggleButton>
      <ToggleButton value="company" sx={{ fontSize: '0.7rem', px: 1.5, textTransform: 'none', gap: 0.5 }}>
        <BusinessIcon sx={{ fontSize: 14 }} /> Company
      </ToggleButton>
    </ToggleButtonGroup>
  );

  if (isMobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>Calendar</Typography>
          {scopeToggle}
        </Box>
        <MobileCalendarView calendarScope={calendarScope} defaultView={defaultView} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>Calendar</Typography>
        {scopeToggle}
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <CalendarView calendarScope={calendarScope} defaultView={defaultView} />
      </Box>
    </Box>
  );
}
